import { useAtomValue } from "jotai";
import { graphNodesAtom, activeFlowIdAtom } from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";

// The slice of a flow's node the edit/remove rows need to diff against. Sourced
// from the live canvas when the approval targets the open flow, otherwise from
// the saved definition fetched by id — so the diff also works from the global
// Genie panel or when a different flow is on screen.
export type ApprovalNode = {
  id: string;
  name: string | null;
  nodeType: string;
  nodeConfig: Record<string, unknown>;
};

export function findNodeByHandle(
  nodes: ApprovalNode[],
  handle: string
): ApprovalNode | undefined {
  const lower = handle.toLowerCase();
  return nodes.find((n) => n.id === handle || n.name?.toLowerCase() === lower);
}

type DefinitionNode = {
  id: string;
  type: string;
  name?: string | null;
  node_config?: Record<string, unknown> | null;
};

export function useApprovalFlowNodes(
  workflowId: string | undefined
): ApprovalNode[] {
  const canvasNodes = useAtomValue(graphNodesAtom);
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  const { spotGroup } = useSpotUser();
  const isOpenFlow = !!workflowId && workflowId === activeFlowId;

  const { data: workflow } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    { path: { group_id: spotGroup.id, workflow_id: workflowId ?? "" } },
    { enabled: !!workflowId && !isOpenFlow }
  );

  if (!workflowId || isOpenFlow) {
    return canvasNodes.map((n) => ({
      id: n.id,
      name: typeof n.data?.name === "string" ? n.data.name : null,
      nodeType: n.data?.nodeType ?? "",
      nodeConfig: (n.data?.nodeConfig ?? {}) as Record<string, unknown>,
    }));
  }

  const definition = workflow?.definition as
    | { nodes?: DefinitionNode[] }
    | undefined;
  const defNodes = definition?.nodes ?? [];
  return defNodes.map((n) => ({
    id: n.id,
    name: n.name ?? null,
    nodeType: n.type,
    nodeConfig: (n.node_config ?? {}) as Record<string, unknown>,
  }));
}
